import type { CatalogApprovalAction, CatalogType } from './catalog-approvals.api';

/**
 * Presentation helpers shared by the Product approvals grid and its detail
 * dialog, so a label never drifts between the row and the modal that opens
 * from it.
 */

export const TYPE_LABEL: Record<CatalogType, string> = {
  ACTIVITY: 'Activity',
  TRANSFER: 'Transfer',
  HOTEL: 'Hotel',
  MEAL_SERVICE: 'Meal',
  VISA: 'Visa',
  INSURANCE: 'Insurance',
  FIXED_DEPARTURE: 'Fixed departure',
  CRUISE: 'Cruise',
};

/** CATALOG_STATUSES (catalog-approval.helper.ts) rendered as UzBadge labels. */
export const CATALOG_STATUS_LABEL: Record<string, string> = {
  DRAFT: 'Draft',
  PENDING_APPROVAL: 'Pending',
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
  ARCHIVED: 'Archived',
};

/** Human labels for the approval-log actions shown in the dialog's trail. */
export const CATALOG_ACTION_LABEL: Record<CatalogApprovalAction | string, string> = {
  SUBMITTED: 'Submitted for approval',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  STATUS_CHANGED: 'Status changed',
  CREATED: 'Created',
  UPDATED: 'Updated',
  MEDIA_PUBLISHED: 'Media published',
  DELETED: 'Deleted',
};

/** Same tone vocabulary the lead-approvals status cell uses. */
export const statusTone = (status: string): 'success' | 'destructive' | 'secondary' | 'outline' => {
  if (status === 'ACTIVE') return 'success';
  if (status === 'INACTIVE' || status === 'ARCHIVED') return 'destructive';
  if (status === 'DRAFT') return 'outline';
  return 'secondary';
};

export const formatWhen = (iso: string | null | undefined): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Where the "Open product" action lands, per catalog type. Every master edits
 * at `/<list>/:id/edit`; fixed departures open their detail page instead - they
 * have no separate edit route.
 */
export const catalogDetailPath = (type: CatalogType, id: string): string => {
  switch (type) {
    case 'ACTIVITY':
      return `/activities/${id}/edit`;
    case 'TRANSFER':
      return `/transfers/${id}/edit`;
    case 'HOTEL':
      return `/hotels/${id}/edit`;
    case 'CRUISE':
      return `/cruises/${id}/edit`;
    case 'MEAL_SERVICE':
      return `/meal-services/${id}/edit`;
    case 'VISA':
      return `/visas/${id}/edit`;
    case 'INSURANCE':
      return `/insurance/${id}/edit`;
    default:
      return `/fixed-departures/${id}`;
  }
};
